import FileSaver from "file-saver";
import { flatten } from "flat";
import { camelCase, difference, intersection, isEmpty } from "lodash-es";
import Papa from "papaparse";
import type React from "react";
import { useState } from "react";
import DownloadButtonContent from "../../../core/buttons/content/DownloadButtonContent";
import { Button, type ButtonProps } from "../../Theme/button";

interface CSVInnerButtonProps<T = Record<string, unknown>> {
  // one of download or data must be specified
  download?: () => Promise<T[]>; // optional promise which returns the data that can be converted in to a csv
  data?: T[]; // optional data which can be converted into a csv.

  filename?: string;
  columnOrder?: string[];
  sortColumns?: boolean;
  disabled?: boolean;
  camelCaseColumns?: boolean;
}

export const unparse = <T = Record<string, unknown>>(
  data: T[],
  columnOrder?: string[],
  sortColumns = true,
  camelCaseColumns = false,
): string => {
  const dataFields: Set<string> = new Set();
  const objects = data.map((d) => {
    const o = flatten<T, Record<string, unknown>>(d, {
      transformKey: camelCaseColumns ? camelCase : undefined,
    });

    for (const k of Object.keys(o)) {
      const value = o[k];
      if (
        value &&
        isEmpty(value) &&
        value !== null &&
        (value as { constructor?: unknown }).constructor === Object
      ) {
        delete o[k];
      } else {
        dataFields.add(k);
      }
    }
    return o;
  });
  let columns: string[];
  if (columnOrder) {
    const orderedColumns = intersection(columnOrder, [...dataFields]);
    const remainder = difference([...dataFields], orderedColumns);

    columns = [...orderedColumns, ...remainder.sort()];
  } else {
    if (sortColumns) {
      columns = [...dataFields].sort();
    } else {
      columns = [...dataFields];
    }
  }

  return Papa.unparse(objects, { columns: columns });
};

const saveDataAsFile = <T = Record<string, unknown>>(
  data: T[],
  filename?: string,
  columnOrder?: string[],
  sortColumns = true,
  camelCaseColumns = true,
): void => {
  FileSaver.saveAs(
    new Blob([unparse(data, columnOrder, sortColumns, camelCaseColumns)], {
      type: "text/csv;charset=utf-8",
    }),
    `${filename ? filename : "export"}.csv`,
  );
};

export type CSVExportButtonProps<T = Record<string, unknown>> =
  CSVInnerButtonProps<T> & Omit<ButtonProps, "outline" | "bright" | "plain">;

const CSVExportButton = <T = Record<string, unknown>>(
  props: CSVExportButtonProps<T>,
): React.ReactElement => {
  const {
    download,
    data,
    filename,
    columnOrder,
    sortColumns = true,
    camelCaseColumns = false,
    color = "brandLight",
    disabled = false,
    ...rest
  } = props;

  const [loading, setLoading] = useState(false);

  const downloadClickHandler = (): void => {
    setLoading(true);
    if (download) {
      download()
        .then((rows) => {
          saveDataAsFile(
            rows,
            filename,
            columnOrder,
            sortColumns,
            camelCaseColumns,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (data) {
      saveDataAsFile(
        data,
        filename,
        columnOrder,
        sortColumns,
        camelCaseColumns,
      );
      setLoading(false);
    } else {
      setLoading(false);
      throw new Error("Data is not available");
    }
  };

  return (
    <Button
      color={color}
      onClick={downloadClickHandler}
      disabled={loading || disabled}
      {...rest}
    >
      <DownloadButtonContent loading={loading} />
    </Button>
  );
};

export default CSVExportButton;
